import { useState } from 'react';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import type { GlossedWord } from '@/features/reader/learningApi';

/**
 * The commute format: one word at a time, one hand, resumable.
 *
 * "I knew this" is **not** stored anywhere. There is no per-user table and
 * no account to attach one to, so the count below is this session only and
 * the screen says so rather than implying a record that does not exist.
 */
export function Flashcards({
  words,
  scriptClass,
  translationScript,
  translationDir,
  onDone,
}: {
  words: readonly GlossedWord[];
  scriptClass: string;
  translationScript: string;
  translationDir: 'ltr' | 'rtl';
  onDone: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [knew, setKnew] = useState(0);

  const card = words[index];

  if (!card) {
    return (
      <div className="rounded-lg border border-border p-8 text-center">
        <p className="font-semibold">That is the set</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-pretty text-muted-foreground">
          {knew} of {words.length} you already knew. There are only so many
          words in one khutbah — running out is finishing, not failing.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          This count is for this sitting only. Progress needs an account, and
          there are none yet.
        </p>
        <Button type="button" size="lg" className="mt-4" onClick={onDone}>
          Back to the week
        </Button>
      </div>
    );
  }

  function next(knewIt: boolean) {
    if (knewIt) setKnew((n) => n + 1);
    setRevealed(false);
    setIndex((i) => i + 1);
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {index + 1} of {words.length}
      </p>

      <div className="flex min-h-56 flex-col items-center justify-center gap-4 rounded-lg border border-border bg-card p-6 text-center">
        <p lang="ar" dir="rtl" className={cn(scriptClass, 'text-4xl font-normal')}>
          {card.surface}
        </p>

        {revealed ? (
          <div className="flex flex-col items-center gap-2">
            {card.transliteration ? (
              <p className="text-sm italic text-muted-foreground">
                {card.transliteration}
              </p>
            ) : null}
            <p dir={translationDir} className={cn(translationScript, 'text-xl')}>
              {card.meaning}
            </p>
            {card.root ? (
              <p
                lang="ar"
                dir="rtl"
                className={cn(scriptClass, 'text-sm tracking-[0.3em] text-muted-foreground')}
              >
                {card.root}
              </p>
            ) : null}
          </div>
        ) : (
          <Button type="button" variant="outline" size="lg" onClick={() => setRevealed(true)}>
            Show the meaning
          </Button>
        )}
      </div>

      {revealed ? (
        <div className="flex gap-2">
          {/* 44px targets: this is a phone format first. */}
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="h-11 flex-1"
            onClick={() => next(false)}
          >
            Not yet
          </Button>
          <Button type="button" size="lg" className="h-11 flex-1" onClick={() => next(true)}>
            I knew this
          </Button>
        </div>
      ) : null}

      <Button type="button" variant="ghost" size="lg" onClick={onDone}>
        Stop for now
      </Button>
    </div>
  );
}
